import {
  Box,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  makeStyles,
  Tooltip,
  Typography,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import WarningIcon from '@material-ui/icons/Warning';
import BuildIcon from '@material-ui/icons/Build';
import CloudIcon from '@material-ui/icons/Cloud';
import PersonIcon from '@material-ui/icons/Person';
import LinkIcon from '@material-ui/icons/Link';
import { InfoCard } from '@backstage/core-components';
import { EntityRefLink, useEntity } from '@backstage/plugin-catalog-react';

// ─── Annotation keys ────────────────────────────────────────────────────────

const CRITICALITY = 'itkom.pbs.ch/criticality';
const DEVELOPMENT_MODE = 'itkom.pbs.ch/development-mode';
const OPERATIONS_MODE = 'itkom.pbs.ch/operations-mode';
const HAS_SLA = 'itkom.pbs.ch/has-sla';
const STRATEGY_REF = 'itkom.pbs.ch/strategy-reference';
const STRATEGY_REF_URL = 'itkom.pbs.ch/strategy-reference_url';
const ROLE_ANNOTATION_PREFIX = 'itkom.pbs.ch/role-';
const CRITICALLY_DEPENDS_ON = 'itkom.pbs.ch/critically-depends-on';
const RELATED_TO = 'itkom.pbs.ch/related-to';

// ─── Types ───────────────────────────────────────────────────────────────────

interface RoleParty {
  role: string;
  refs: string[];
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles(theme => ({
  section: {
    marginTop: theme.spacing(1.5),
  },
  sectionTitle: {
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  chip: {
    marginRight: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  criticalityHigh: {
    backgroundColor: '#f44336',
    color: '#fff',
    fontWeight: 700,
  },
  criticalityMedium: {
    backgroundColor: '#ff9800',
    color: '#fff',
    fontWeight: 700,
  },
  criticalityLow: {
    backgroundColor: '#4caf50',
    color: '#fff',
    fontWeight: 700,
  },
  devInternal: {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
  },
  devExternal: {
    backgroundColor: theme.palette.grey[400],
    color: theme.palette.getContrastText(theme.palette.grey[400]),
  },
  devOpenSource: {
    backgroundColor: '#795548',
    color: '#fff',
  },
  opsInternal: {
    backgroundColor: '#1976d2',
    color: '#fff',
  },
  opsExternal: {
    backgroundColor: theme.palette.grey[500],
    color: '#fff',
  },
  opsBetriebsplattform: {
    backgroundColor: '#0288d1',
    color: '#fff',
  },
  slaTrue: {
    color: '#4caf50',
    verticalAlign: 'middle',
    marginRight: 4,
  },
  slaFalse: {
    color: theme.palette.text.disabled,
    verticalAlign: 'middle',
    marginRight: 4,
  },
  depCritical: {
    borderLeft: '3px solid #f44336',
    paddingLeft: theme.spacing(1),
  },
  depNormal: {
    borderLeft: '3px solid #ff9800',
    paddingLeft: theme.spacing(1),
  },
  depRelated: {
    borderLeft: `3px solid ${theme.palette.divider}`,
    paddingLeft: theme.spacing(1),
  },
  listItem: {
    paddingTop: 2,
    paddingBottom: 2,
    paddingLeft: 0,
  },
  strategyRef: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    whiteSpace: 'pre-line',
  },
}));

// ─── Helpers ─────────────────────────────────────────────────────────────────

function parseRoleAnnotations(annotations: Record<string, string>): RoleParty[] {
  return Object.entries(annotations)
    .filter(([key]) => key.startsWith(ROLE_ANNOTATION_PREFIX))
    .map(([key, value]) => {
      const suffix = key.slice(ROLE_ANNOTATION_PREFIX.length);
      const role = suffix
        .split('-')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
      const refs = value.split(',').map(s => s.trim()).filter(Boolean);
      return { role, refs };
    });
}

function splitNames(raw: string | undefined): string[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
}

function systemRef(name: string): string {
  return `system:default/${name}`;
}

const DEV_MODE_LABELS: Record<string, string> = {
  internal: 'Intern',
  external: 'Extern',
  open_source: 'Open Source',
};

const OPS_MODE_LABELS: Record<string, string> = {
  internal: 'Intern',
  external: 'Extern',
  betriebsplattform: 'Betriebsplattform',
};

// ─── Component ───────────────────────────────────────────────────────────────

export const ServiceInfoCard = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations ?? {};

  const criticality = annotations[CRITICALITY];
  const devMode = annotations[DEVELOPMENT_MODE];
  const opsMode = annotations[OPERATIONS_MODE];
  const hasSla = annotations[HAS_SLA];
  const strategyRef = annotations[STRATEGY_REF];
  const strategyRefUrl = annotations[STRATEGY_REF_URL];
  const roleParties = parseRoleAnnotations(annotations);
  const criticalDeps = splitNames(annotations[CRITICALLY_DEPENDS_ON]);
  const normalDeps = (entity.spec?.dependsOn as string[] | undefined) ?? [];
  const relatedTo = splitNames(annotations[RELATED_TO]);

  const criticalityClass =
    criticality === 'high'
      ? classes.criticalityHigh
      : criticality === 'medium'
        ? classes.criticalityMedium
        : criticality === 'low'
          ? classes.criticalityLow
          : undefined;

  const devModeClass =
    devMode === 'internal'
      ? classes.devInternal
      : devMode === 'open_source'
        ? classes.devOpenSource
        : classes.devExternal;

  const opsModeClass =
    opsMode === 'internal'
      ? classes.opsInternal
      : opsMode === 'betriebsplattform'
        ? classes.opsBetriebsplattform
        : classes.opsExternal;

  const hasDeps =
    criticalDeps.length > 0 || normalDeps.length > 0 || relatedTo.length > 0;

  return (
    <InfoCard title="Service Info">
      {/* ── Row 1: Criticality / Dev / Ops ── */}
      <Grid container spacing={2}>
        {criticality && (
          <Grid item>
            <Typography className={classes.sectionTitle}>Criticality</Typography>
            <Chip
              size="small"
              label={criticality.toUpperCase()}
              className={`${classes.chip} ${criticalityClass}`}
              icon={<WarningIcon style={{ color: 'inherit', fontSize: '0.9rem' }} />}
            />
          </Grid>
        )}
        {devMode && (
          <Grid item>
            <Typography className={classes.sectionTitle}>Development</Typography>
            <Chip
              size="small"
              label={DEV_MODE_LABELS[devMode] ?? devMode}
              className={`${classes.chip} ${devModeClass}`}
              icon={<BuildIcon style={{ color: 'inherit', fontSize: '0.9rem' }} />}
            />
          </Grid>
        )}
        {opsMode && (
          <Grid item>
            <Typography className={classes.sectionTitle}>Operations</Typography>
            <Chip
              size="small"
              label={OPS_MODE_LABELS[opsMode] ?? opsMode}
              className={`${classes.chip} ${opsModeClass}`}
              icon={<CloudIcon style={{ color: 'inherit', fontSize: '0.9rem' }} />}
            />
          </Grid>
        )}
        <Grid item>
          <Typography className={classes.sectionTitle}>SLA</Typography>
          <Box display="flex" alignItems="center">
            {hasSla === 'true' ? (
              <Tooltip title="Service Level Agreement vorhanden">
                <CheckCircleIcon fontSize="small" className={classes.slaTrue} />
              </Tooltip>
            ) : (
              <Tooltip title="Kein SLA definiert">
                <CancelIcon fontSize="small" className={classes.slaFalse} />
              </Tooltip>
            )}
            <Typography variant="body2" style={{ fontSize: '0.8rem' }}>
              {hasSla === 'true' ? 'Ja' : 'Nein'}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* ── Strategy Reference ── */}
      {(strategyRef || strategyRefUrl) && (
        <>
          <Divider className={classes.section} />
          <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>Strategiebezug</Typography>
            {strategyRef && (
              <Typography className={classes.strategyRef}>{strategyRef}</Typography>
            )}
            {strategyRefUrl && (
              <Typography variant="body2" style={{ marginTop: 4 }}>
                <a href={strategyRefUrl} target="_blank" rel="noopener noreferrer">
                  {strategyRefUrl}
                </a>
              </Typography>
            )}
          </Box>
        </>
      )}

      {/* ── Involved Parties ── */}
      {roleParties.length > 0 && (
        <>
          <Divider className={classes.section} />
          <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>
              <PersonIcon style={{ fontSize: '0.9rem', verticalAlign: 'middle', marginRight: 4 }} />
              Beteiligte Parteien
            </Typography>
            <List dense disablePadding>
              {roleParties.flatMap(({ role, refs }) =>
                refs.map((ref, i) => (
                  <ListItem key={`${role}-${i}`} className={classes.listItem}>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                          <EntityRefLink entityRef={ref} />
                          <Typography
                            variant="caption"
                            style={{ color: '#666', fontSize: '0.75rem' }}
                          >
                            · {role}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))
              )}
            </List>
          </Box>
        </>
      )}

      {/* ── Service Dependencies ── */}
      {hasDeps && (
        <>
          <Divider className={classes.section} />
          <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>
              <LinkIcon style={{ fontSize: '0.9rem', verticalAlign: 'middle', marginRight: 4 }} />
              Service-Abhaengigkeiten
            </Typography>
            <List dense disablePadding>
              {criticalDeps.map(name => (
                <ListItem key={`c-${name}`} className={`${classes.listItem} ${classes.depCritical}`}>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                        <EntityRefLink entityRef={systemRef(name)} />
                        <Chip
                          size="small"
                          label="kritisch"
                          style={{ backgroundColor: '#f44336', color: '#fff', fontSize: '0.65rem', height: 18 }}
                        />
                      </Box>
                    }
                  />
                </ListItem>
              ))}
              {normalDeps.map(ref => (
                <ListItem key={`n-${ref}`} className={`${classes.listItem} ${classes.depNormal}`}>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                        <EntityRefLink entityRef={ref} />
                        <Chip
                          size="small"
                          label="benoetigt"
                          style={{ backgroundColor: '#ff9800', color: '#fff', fontSize: '0.65rem', height: 18 }}
                        />
                      </Box>
                    }
                  />
                </ListItem>
              ))}
              {relatedTo.map(name => (
                <ListItem key={`r-${name}`} className={`${classes.listItem} ${classes.depRelated}`}>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                        <EntityRefLink entityRef={systemRef(name)} />
                        <Chip
                          size="small"
                          label="verwandt"
                          style={{ fontSize: '0.65rem', height: 18 }}
                        />
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </>
      )}
    </InfoCard>
  );
};
